import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "w-full my-4"
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "flex gap-1.5 flex-wrap mb-2 w-full justify-center md:justify-end" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = {
  key: 0,
  class: "flex items-center px-0.5"
}
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "flex items-start gap-2" }
const _hoisted_8 = { class: "flex flex-none items-center gap-2" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  class: "grow overflow-hidden rounded-2xl border border-[#e7ddcc] bg-white"
}
const _hoisted_12 = {
  key: 0,
  class: "border-t border-[#e7ddcc] py-1"
}
const _hoisted_13 = ["onClick"]

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading
  from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const variantIdx = ""


export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3StonesNumber',
  props: {
    groupIdx: {},
    hideSpreading: { type: Boolean, default: false }
  },
  emits: ["patch"],
  setup(__props: any, { emit: __emit }) {


const props = __props;
const emit = __emit;

const store = useStore();
const {t} = useI18n();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred convention from itemV3StonesSetting.vue / itemV3StonesStoneSize.vue:
 * configureAll/itemIdx are wired into ParametersVariations the same way, but the variants
 * are fetched per stone group via getItemVariants(itemIdx, "stones.<groupIdx>.stone_number")
 * / getItemVariants(itemIdx, "stones.<groupIdx>.stone_spreading").
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone number - stones.<groupIdx>.stone_number
 * stone spreading - stones.<groupIdx>.stone_spreading
 */

const stoneNumber = ref({
  "stone_number": {},
  "stone_spreading": {}
} as any)

const getStoneNumberData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneNumber.value.stone_number = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_number")
    stoneNumber.value.stone_spreading = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_spreading")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneNumberData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneNumberData(props.groupIdx)
}, {deep: true})

/**
 * stone number variants
 */

const numberActive = computed(() => {
  return stoneNumber.value.stone_number[0]
})

const numberValuesList = computed(() => numberActive.value?.values || [])

const matchedNumberValue = computed(() => numberValuesList.value.find((v: any) => v.match))

// pre-selected stone numbers, always shown first (in this order) when available
const quickNumberValues = [1, 3, 5, 7]

const getQuickNumberValue = (idx: number) => numberValuesList.value.find((v: any) => v.idx === idx)

const isValidNumberIdx = (idx: number) => numberValuesList.value.some((v: any) => v.idx === idx)

/**
 * stone number - step stepper (transferred from itemV3StonesStoneSize.vue's "stone size -
 * step slider"), lets the user move to the previous/next valid stone number instead of only
 * being able to pick one of the quick values or type a number manually
 */

const sortedNumberIdxs = computed(() => numberValuesList.value
    .map((v: any) => Number(v.idx))
    .sort((a: number, b: number) => a - b))

const currentNumberPos = computed(() => {
  if (!matchedNumberValue.value) return -1
  return sortedNumberIdxs.value.indexOf(Number(matchedNumberValue.value.idx))
})

const stepDownDisabled = computed(() => currentNumberPos.value <= 0)

const stepUpDisabled = computed(() => currentNumberPos.value !== -1
    ? currentNumberPos.value >= sortedNumberIdxs.value.length - 1
    : sortedNumberIdxs.value.length === 0)

const stepNumber = (delta: number) => {
  if (!numberActive.value || !sortedNumberIdxs.value.length) return

  const newPos = currentNumberPos.value === -1 ? 0 : currentNumberPos.value + delta
  if (newPos < 0 || newPos >= sortedNumberIdxs.value.length) return

  const newIdx = sortedNumberIdxs.value[newPos]
  const newValue = numberValuesList.value.find((v: any) => Number(v.idx) === newIdx)
  if (newValue) handleValuePatch(numberActive.value.idx, newValue)
}

/**
 * stone spreading variants
 */

const spreadingActive = computed(() => stoneNumber.value.stone_spreading[0])

const spreadingValuesList = computed(() => spreadingActive.value?.values || [])

// "full"/"half"/"three-quarters" are shown as quick options next to the stone number
// quick picks, with fixed translated display names (independent of the variant's own title)
const quickSpreadingIdx = ["full", "half", "three-quarters"]

const quickSpreadingLabels = computed<Record<string, string>>(() => ({
  full: t('components.parameters.stones.number.spreading_full'),
  half: t('components.parameters.stones.number.spreading_half'),
  "three-quarters": t('components.parameters.stones.number.spreading_three_quarters'),
}))

const getQuickSpreadingValue = (idx: string) => spreadingValuesList.value.find((v: any) => v.idx === idx)

// the remaining spreading options are hidden behind a dropdown (transferred from
// itemV3StonesPosition.vue's "position - object" dropdown)
const otherSpreadingValues = computed(() => spreadingValuesList.value.filter((v: any) => !quickSpreadingIdx.includes(v.idx)))

const matchedOtherSpreadingValue = computed(() => otherSpreadingValues.value.find((v: any) => v.match))

// whether the dropdown with the remaining spreading options is open
const showSpreadingDropdown = ref(false)

/**
 * manual stone number input - allows any stone number that is a valid value idx in stone_number
 */

const manualNumber = ref(0)

watch(matchedNumberValue, (value) => {
  manualNumber.value = value ? value.idx : ""
}, {immediate: true})

const onManualNumberChange = () => {
  const idx: number = Number(manualNumber.value) || 0

  if (idx && isValidNumberIdx(idx)) {
    if (!matchedNumberValue.value || matchedNumberValue.value.idx !== idx) {
      handleValuePatch(numberActive.value.idx, {idx: idx})
    }
  } else {
    // not a valid stone number option - reset back to the currently matched value
    manualNumber.value = matchedNumberValue.value ? matchedNumberValue.value.idx : ""
  }
}

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};


return (_ctx: any,_cache: any) => {
  return (numberActive.value || spreadingActive.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (numberActive.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("ul", _hoisted_3, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(quickNumberValues, (numberIdx) => {
                  return (_openBlock(), _createElementBlock(_Fragment, null, [
                    (getQuickNumberValue(numberIdx))
                      ? (_openBlock(), _createElementBlock("li", {
                          key: 'number-' + numberIdx
                        }, [
                          _createElementVNode("button", {
                            type: "button",
                            class: _normalizeClass(["flex size-9 cursor-pointer items-center justify-center rounded-full border text-xs font-medium transition-colors", getQuickNumberValue(numberIdx).match ? 'border-2 border-brand-main bg-[#f3f0e9] text-brand-main' : 'border-[#e7ddcc] text-parameter-text hover:bg-[#f3f0e9]']),
                            onClick: ($event: any) => (handleValuePatch(numberActive.value.idx, getQuickNumberValue(numberIdx)))
                          }, _toDisplayString(numberIdx), 11, _hoisted_4)
                        ]))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 64)),
                (quickSpreadingIdx.some((spreadingIdx) => getQuickSpreadingValue(spreadingIdx)))
                  ? (_openBlock(), _createElementBlock("li", _hoisted_5, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("span", { class: "h-6 w-px bg-gray-300" }, null, -1)
                    ]))]))
                  : _createCommentVNode("", true),
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(quickSpreadingIdx, (spreadingIdx) => {
                  return (_openBlock(), _createElementBlock(_Fragment, null, [
                    (getQuickSpreadingValue(spreadingIdx))
                      ? (_openBlock(), _createElementBlock("li", {
                          key: 'spreading-' + spreadingIdx
                        }, [
                          _createElementVNode("button", {
                            type: "button",
                            class: _normalizeClass(["flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors", getQuickSpreadingValue(spreadingIdx).match ? 'border-2 border-brand-main bg-[#f3f0e9] text-brand-main' : 'border-[#e7ddcc] text-parameter-text hover:bg-[#f3f0e9]']),
                            onClick: ($event: any) => (handleValuePatch(spreadingActive.value.idx, getQuickSpreadingValue(spreadingIdx)))
                          }, _toDisplayString(quickSpreadingLabels.value[spreadingIdx]), 11, _hoisted_6)
                        ]))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 64))
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30",
                    disabled: stepDownDisabled.value,
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (stepNumber(-1)))
                  }, " − ", 8, _hoisted_9),
                  _withDirectives(_createElementVNode("input", {
                    type: "text",
                    inputmode: "numeric",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((manualNumber).value = $event)),
                    onChange: onManualNumberChange,
                    onKeyup: _withKeys(onManualNumberChange, ["enter"]),
                    class: "flex-1 rounded-xl w-15 border border-[#e7ddcc] py-1.5 px-3 text-center text-xs font-medium text-parameter-title focus:border-brand-main focus:outline-none"
                  }, null, 544), [
                    [_vModelText, manualNumber.value]
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30",
                    disabled: stepUpDisabled.value,
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (stepNumber(1)))
                  }, " + ", 8, _hoisted_10)
                ]),
                (!__props.hideSpreading && spreadingActive.value && otherSpreadingValues.value.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-xs text-parameter-title transition-colors hover:bg-[#f3f0e9]",
                        onClick: _cache[3] || (_cache[3] = ($event: any) => (showSpreadingDropdown.value = !showSpreadingDropdown.value))
                      }, [
                        _createElementVNode("span", null, _toDisplayString(matchedOtherSpreadingValue.value?.title || _ctx.$t('components.parameters.stones.number.spreading_fallback')), 1),
                        (_openBlock(), _createElementBlock("svg", {
                          xmlns: "http://www.w3.org/2000/svg",
                          class: _normalizeClass(["size-3.5 shrink-0 transition-transform", showSpreadingDropdown.value ? 'rotate-180' : '']),
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2",
                          "stroke-linecap": "round",
                          "stroke-linejoin": "round"
                        }, [...(_cache[5] || (_cache[5] = [
                          _createElementVNode("polyline", { points: "6 9 12 15 18 9" }, null, -1)
                        ]))], 2))
                      ]),
                      (showSpreadingDropdown.value)
                        ? (_openBlock(), _createElementBlock("ul", _hoisted_12, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otherSpreadingValues.value, (variantData, otherSpreadingIdx) => {
                              return (_openBlock(), _createElementBlock("li", {
                                key: otherSpreadingIdx,
                                class: _normalizeClass(["mx-1.5 cursor-pointer rounded-xl px-2.5 py-2 text-xs transition-colors hover:bg-[#f3f0e9]", variantData.match ? 'bg-[#e7ddcc] font-medium text-parameter-title' : 'text-parameter-text']),
                                onClick: ($event: any) => {handleValuePatch(spreadingActive.value.idx, variantData); showSpreadingDropdown.value = false}
                              }, _toDisplayString(variantData.title), 11, _hoisted_13))
                            }), 128))
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]))
          : _createCommentVNode("", true)
      ]))
    : (_openBlock(), _createBlock(ItemV3StonesParameterLoading, { key: 1 }))
}
}

})